import { useContext, MouseEvent, useState, useEffect, ReactNode } from "react";

import { styled, alpha } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteConversationFromApi,
  shareConversation,
  updateConversationNameFromApi,
} from "../../services/chatService";
import {
  removeConversation,
  setCurrentConversationName,
} from "modules/chat/store/conversationList";
import {
  BootstrapDialog,
  BootstrapDialogInnerContainer,
  ChatBotTextArea,
  ChatBotTextInput,
  DialogActionsButtonContainer,
  HistoryItem,
  HistoryItemStyledMenuContainer,
} from "../chat.styled";
import { Toast as showToast } from "core/components/global/toast/toast";
import {
  AdminModal,
  AdminModalInnerContainer,
  AdminTitle,
  AdminModalBody,
  AdminGroupsForm,
  AdminFormGroup,
  ValidatorText,
  AdminModalButtonContainer,
} from "modules/agentMySecurity/components/admin/admin.styled";
import {
  Avatar,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Modal,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { ConversationType } from "../chat.type";
import { useAppDispatch } from "core/store/reduxStore";
import { AuthContext } from "core/contexts/auth/auth";
import axios from "axios";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { useCopyToClipboard } from "core/hooks/copytoClipboard";
import AccentureIcon from "assets/AccentureIcon";
import DeleteHistoryItemModal from "./deleteHistoryItemModal";
import { BootstrapDialogTitle } from "core/components/global/bootstrap/dialogTitle";
import { useTheme } from "styled-components";

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
}

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "left",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "left",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    border: "none",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "0 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

export default function ConversationMenuItem({
  conversation,
  setConversationFromHistory,
  handleOpenMenu,
  convoState,
  highlightedConversation,
  setHighlightedConversation,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditModalOpen, setisEditModalOpen] = useState(false);
  const [showConvoValidator, setShowConvoValidator] = useState<boolean>(false);
  const [selectedConvo, setSelectedConvo] =
    useState<ConversationType>(conversation);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");

  const [selectedConvoName, setSelectedConvoName] = useState(
    conversation.conversationTitle
  );
  const [copiedText, copyToClipboard] = useCopyToClipboard();
  const [openModal, setOpenModal] = useState(false);
  const { checkAMSToken } = useContext(AuthContext);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    console.log("handleClick");
    if (conversation.conversationId) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSelectConversation = (id, model) => {
    setHighlightedConversation(conversation.conversationId);
    setConversationFromHistory(id, model);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleOpenTitleEdit(action, conversation) {
    setisEditModalOpen(action);
  }

  function handleSetEditModalClosed() {
    handleClose();
    setisEditModalOpen(false);
  }

  async function deleteConvoAsync(id: string) {
    const token = await checkAMSToken();

    const response = await deleteConversationFromApi(token, id);

    // Update the frontend if the conversation is truly deleted
    if (response) {
      if (response.status < 300) {
        // 4)Remove the conversation from the Conversation Redux Slice
        dispatch(removeConversation({ conversationId: id }));

        // Close the menu
        handleClose();
      }
    }
  }

  /**
   * Delete the selected conversation
   */
  const deleteConversation = async (id: string) => {
    if (id) {
      // The conversation must be cleared
      // 1) Delete the conversation in the backend
      await deleteConvoAsync(id);
      handleClose();
    }
  };

  /**
   * Updated the conversation ID
   * @param conversationId
   */
  const handleShareConversation = async (conversationId) => {
    const token = await checkAMSToken();
    const link = await shareConversation(token, conversationId);

    if (link) {
      setShareLink(link);
      setIsShareModalOpen(true);
      handleClose();
    } else {
      showToast("Unable to generate share link", "error");
    }
  };

  /**
   * Update the name of a conversation
   */
  const handleSubmitNewName = async () => {
    const token = await checkAMSToken();

    if (selectedConvoName.length === 0 || selectedConvoName.length > 30) {
      setShowConvoValidator(true);
    } else {
      if (selectedConvo && selectedConvo.conversationId) {
        try {
          const response = await updateConversationNameFromApi(
            token,
            selectedConvo.conversationId,
            selectedConvoName
          );

          if (response && response.status < 300) {
            dispatch(
              setCurrentConversationName({
                conversationId: selectedConvo.conversationId,
                conversationTitle: selectedConvoName,
              })
            );

            // Close the modal
            handleSetEditModalClosed();
          }
        } catch (error) {
          showToast(
            `Error updating conversation with name ${selectedConvoName}`,
            "error"
          );
        }
      }
    }
  };

  // Open and close modal handlers
  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    handleClose();
  };

  // Delete conversation and close modal
  const handleDelete = () => {
    deleteConversation(conversation.conversationId);
    handleModalClose();
  };

  useEffect(() => {
    if (highlightedConversation === conversation.conversationId) {
      console.log(
        (highlightedConversation === conversation.conversationId).toString()
      );
    }
  }, [highlightedConversation]);

  return (
    <HistoryItem
      isselected={(
        highlightedConversation === conversation.conversationId
      ).toString()}
      key={`history-${conversation.conversationId}`}
      onClick={() =>
        handleSelectConversation(
          conversation.conversationId,
          conversation.model
        )
      }
    >
      <div className="d-flex flex-row justify-content-between align-items-center">
        <div className="fade-wrapper w-100 d-flex align-items-center">
          <p className="cursor-pointer">
            {conversation.conversationTitle.substring(0, 30)}
          </p>
        </div>
        <div
          className={`dot-container ${
            open &&
            highlightedConversation === conversation.conversationId &&
            "highlight-dots"
          }`}
          onClick={handleClick}
        >
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
        </div>

        <StyledMenu
          id="demo-customized-menu"
          className="demo-customized-menu"
          MenuListProps={{
            "aria-labelledby": "demo-customized-button",
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <HistoryItemStyledMenuContainer className="w-100 h-100">
            <div
              className="d-flex flex-row p-1 cursor-pointer"
              onClick={() => handleOpenTitleEdit(true, conversation)}
            >
              <EditIcon />
              <p className="ms-2">Edit</p>
            </div>
            <div
              className="d-flex flex-row p-1 cursor-pointer"
              onClick={() =>
                handleShareConversation(conversation.conversationId)
              }
            >
              <ShareOutlinedIcon />
              <p className="ms-2">Share</p>
            </div>
            <div
              className="d-flex flex-row p-1 cursor-pointer"
              onClick={handleModalOpen}
            >
              <DeleteIcon />
              <p className="ms-2">Delete</p>
            </div>
          </HistoryItemStyledMenuContainer>
        </StyledMenu>
        <DeleteHistoryItemModal
          openModal={openModal}
          handleModalClose={handleModalClose}
          handleDelete={handleDelete}
        />
      </div>

      {/* Share Link Dialog */}
      <BootstrapDialog
        onClose={() => setIsShareModalOpen(false)}
        sx={{ borderRadius: "1rem" }}
        aria-labelledby="share-modal-title"
        aria-describedby="share-modal-description"
        open={isShareModalOpen}
      >
        <BootstrapDialogInnerContainer elevation={2} className="p-2">
          <BootstrapDialogTitle
            id="share-dialog-title"
            onClose={() => setIsShareModalOpen(false)}
            customTheme={theme}
          >
            <h3>Share Conversation</h3>
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatBotTextInput
                value={shareLink}
                className="custom-input flex-1"
              />
              <Button
                variant="contained"
                className="primary-color-btn"
                onClick={async () => {
                  const success = await copyToClipboard(shareLink);
                  if (success) {
                    showToast("Link copied to clipboard!", "success");
                  } else {
                    showToast(
                      "Copy failed, no permission or clipboard support",
                      "error"
                    );
                  }
                }}
              >
                Copy
              </Button>
            </Box>
          </DialogContent>
          <DialogActionsButtonContainer>
            <Button
              onClick={() => setIsShareModalOpen(false)}
              variant="contained"
              className="primary-color-btn"
            >
              Close
            </Button>
          </DialogActionsButtonContainer>
        </BootstrapDialogInnerContainer>
      </BootstrapDialog>

      {/* Code for Feedback dialog box  */}
      <BootstrapDialog
        onClose={() => handleOpenMenu(false)}
        sx={{ borderRadius: "1rem" }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        open={isEditModalOpen}
      >
        <BootstrapDialogInnerContainer elevation={2}>
          <BootstrapDialogTitle
            id="feedback-dialog-title"
            onClose={() => setisEditModalOpen(false)}
            customTheme={theme}
          >
            <h3 className="ps-1">Update Conversation Name</h3>
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <>
              <ChatBotTextInput
                placeholder={selectedConvo?.conversationTitle || ""}
                value={selectedConvoName}
                className="custom-input"
                name="name"
                onChange={(event) => setSelectedConvoName(event.target.value)}
                aria-placeholder="Enter a new name name"
              />
            </>
            {showConvoValidator && (
              <ValidatorText>
                <p>The name must be between 1 and 30 characters</p>
              </ValidatorText>
            )}
          </DialogContent>
          <DialogActionsButtonContainer>
            <Button
              autoFocus
              onClick={() => handleSubmitNewName()}
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActionsButtonContainer>
        </BootstrapDialogInnerContainer>
      </BootstrapDialog>
    </HistoryItem>
  );
}
